import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronRight } from "lucide-react";
import type { GraphNode } from "./types";

interface TreeFile { id: string; name: string; title: string }
interface TreeFolder { name: string; path: string; folders: TreeFolder[]; files: TreeFile[] }

// Как в Obsidian: папки и файлы отдельными группами, внутри — по алфавиту с учётом кириллицы.
const collator = new Intl.Collator("ru", { sensitivity: "base", numeric: true });

function buildTree(nodes: GraphNode[], rootName: string): TreeFolder {
  const root: TreeFolder = { name: rootName, path: "", folders: [], files: [] };
  const index = new Map<string, TreeFolder>([["", root]]);
  const ensure = (path: string): TreeFolder => {
    const existing = index.get(path);
    if (existing) return existing;
    const cut = path.lastIndexOf("/");
    const parent = ensure(cut === -1 ? "" : path.slice(0, cut));
    const folder: TreeFolder = { name: path.slice(cut + 1), path, folders: [], files: [] };
    parent.folders.push(folder);
    index.set(path, folder);
    return folder;
  };
  for (const node of nodes) {
    const cut = node.id.lastIndexOf("/");
    // Имя файла, а не заголовок из frontmatter: список должен совпадать с проводником Obsidian.
    ensure(cut === -1 ? "" : node.id.slice(0, cut)).files.push({ id: node.id, name: node.id.slice(cut + 1), title: node.title });
  }
  const sort = (folder: TreeFolder): void => {
    folder.folders.sort((first, second) => collator.compare(first.name, second.name));
    folder.files.sort((first, second) => collator.compare(first.name, second.name));
    for (const child of folder.folders) sort(child);
  };
  sort(root);
  return root;
}

function ancestors(id: string): string[] {
  const parts = id.split("/").slice(0, -1);
  const result = [""];
  let path = "";
  for (const part of parts) {
    path = path ? `${path}/${part}` : part;
    result.push(path);
  }
  return result;
}

export function FileTree({ nodes, activeId, rootName = "Zazerkalye", onOpen }: { nodes: GraphNode[]; activeId?: string; rootName?: string; onOpen: (id: string) => void }) {
  const tree = useMemo(() => buildTree(nodes, rootName), [nodes, rootName]);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set([""]));
  const activeRef = useRef<HTMLButtonElement>(null);

  // Открытая заметка всегда видна: её папки раскрываются, а строка подкручивается в зону видимости.
  useEffect(() => {
    if (!activeId) return;
    setExpanded((current) => {
      const missing = ancestors(activeId).filter((path) => !current.has(path));
      if (!missing.length) return current;
      const next = new Set(current);
      for (const path of missing) next.add(path);
      return next;
    });
  }, [activeId]);
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeId, expanded]);

  const toggle = (path: string): void => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(path)) next.add(path);
      return next;
    });
  };

  const renderFolder = (folder: TreeFolder, depth: number) => {
    const open = expanded.has(folder.path);
    const count = folder.files.length + folder.folders.length;
    return (
      <div className="tree-branch" key={folder.path || " root"}>
        <button
          type="button"
          className="tree-row tree-folder"
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          aria-expanded={open}
          onClick={() => toggle(folder.path)}
        >
          <ChevronRight size={13} className={`tree-chevron ${open ? "open" : ""}`} aria-hidden />
          <span className="tree-label">{folder.name}</span>
          <small>{count}</small>
        </button>
        {open ? (
          <div className="tree-children">
            {folder.folders.map((child) => renderFolder(child, depth + 1))}
            {folder.files.map((file) => (
              <button
                type="button"
                key={file.id}
                ref={file.id === activeId ? activeRef : undefined}
                className={`tree-row tree-file ${file.id === activeId ? "active" : ""}`}
                style={{ paddingLeft: `${8 + (depth + 1) * 16 + 13}px` }}
                title={file.title}
                aria-current={file.id === activeId ? "page" : undefined}
                onClick={() => onOpen(file.id)}
              >
                <span className="tree-label">{file.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <nav className="file-tree" aria-label="Список заметок">
      {renderFolder(tree, 0)}
    </nav>
  );
}
